// English locale for panel. Keys must mirror src/i18n/ru.ts —
// see src/i18n/index.tsx for type-level enforcement.

import type { ru } from './ru';

export const en: Record<keyof typeof ru, string> = {
  // Common
  'common.loading': 'Loading…',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.create': 'Create',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'common.edit': 'Edit',
  'common.copy': 'copy',
  'common.copied': 'copied',
  'common.error': 'Error',
  'common.actions': 'Actions',
  'common.from': 'From',
  'common.to': 'To',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.search': 'Search',
  'common.unknown': 'unknown',
  'common.never': 'never',
  'common.empty': 'Empty',
  'common.notLoaded': 'Session not loaded',
  'common.reset': 'Reset',
  'offline.banner': 'No network connection. Showing the last cached snapshot of conversations.',
  'desktop.update.available': 'App version {version} is available',
  'desktop.update.install': 'Update',
  'desktop.update.installing': 'Updating…',
  'common.unknownError': 'Unknown error',
  'common.errorPrefix': 'Error',
  'common.readonly': 'read-only',
  'common.hide': 'Hide',

  // Nav
  'nav.brand': 'Anexica',
  'nav.conversations': 'Conversations',
  'nav.liveVisitors': 'Live visitors',
  'nav.analytics': 'Analytics',
  'nav.cannedResponses': 'Canned',
  'nav.exports': 'Exports',
  'nav.sites': 'Sites',
  'nav.webhooks': 'Webhooks',
  'nav.audit': 'Audit',
  'nav.reports': 'Reports',
  'nav.billing': 'Billing',
  'nav.settings': 'Settings',
  'liveVisitors.title': 'Live visitors',
  'liveVisitors.hint': 'Visitors currently on your site. Updates every few seconds.',
  'liveVisitors.counts.now': 'On site now',
  'liveVisitors.counts.last5min': 'Last 5 min',
  'liveVisitors.counts.lastHour': 'Last hour',
  'liveVisitors.counts.today': 'Today',
  'liveVisitors.empty': 'No visitors on the site right now',
  'liveVisitors.anonymous': 'Anonymous visitor',
  'liveVisitors.writeFirst': 'Message first',
  'liveVisitors.openConversation': 'Open chat',
  'liveVisitors.outreachPlaceholder': 'Write the first message…',
  'liveVisitors.send': 'Send',
  'liveVisitors.sending': 'Sending…',
  'liveVisitors.outreachError': 'Could not send, try again',
  'nav.logout': 'Log out',
  'theme.toggle': 'Toggle theme',
  'theme.light': 'Light theme',
  'theme.dark': 'Dark theme',

  // Operator status (ANX-071)
  'status.label': 'Status',
  'status.online': 'Online',
  'status.away': 'Away',
  'status.busy': 'Busy',
  'status.offline': 'Offline',

  // Auth / Login
  'auth.title': 'Sign in to Anexica',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.tenant': 'Organization',
  'auth.tenantHint': 'Organization slug — from your invite',
  'auth.forgotPassword': 'Forgot password?',
  'auth.sessionExpired': 'Your session has expired. Please sign in again.',
  'auth.backToLogin': '← Back to sign in',
  'auth.submit': 'Sign in',
  'auth.submitting': 'Signing in…',
  'auth.error.invalidCredentials': 'Invalid email, password, or organization',
  'auth.error.tenantSuspended': 'Tenant suspended — contact your admin',
  'auth.error.forbidden': 'Access denied',
  'auth.error.rateLimited': 'Too many attempts, please wait a minute',
  'auth.error.network': 'Network problem — check your connection',
  'auth.error.invalidPayload': 'Fill in all fields',
  'auth.error.generic': 'Sign-in failed',
  // ANX-102 — password reset
  'auth.forgot.title': 'Reset your password',
  'auth.forgot.subtitle': "Enter your email and organization — we'll email you a reset link.",
  'auth.forgot.submit': 'Send reset link',
  'auth.forgot.submitting': 'Sending…',
  'auth.forgot.sent':
    "If an account exists, we've emailed a reset link. Check your inbox (and spam folder).",
  'auth.reset.title': 'Reset password',
  'auth.reset.subtitle': 'Choose a new password for your account.',
  'auth.reset.password': 'New password',
  'auth.reset.confirm': 'Confirm password',
  'auth.reset.submit': 'Save password',
  'auth.reset.submitting': 'Saving…',
  'auth.reset.success': 'Password changed. Sign in with your new password.',
  'auth.reset.toLogin': 'Sign in',
  'auth.reset.mismatch': "Passwords don't match",
  'auth.reset.tooShort': 'Password must be at least 8 characters',
  'auth.reset.invalidToken': 'This link is invalid or has expired.',
  'auth.reset.requestNew': 'Request a new link',

  // Conversations list
  'conversations.title': 'Conversations',
  'conversations.empty': 'No conversations',
  'conversations.emptyOpen': 'No open conversations.',
  'conversations.emptyClosed': 'No closed conversations.',
  'conversations.filter.all': 'All',
  'conversations.filter.open': 'Open',
  'conversations.filter.closed': 'Closed',
  'conversations.filter.mine': 'Mine',
  'conversations.lastMessage': 'Last message',
  'conversations.assignedTo': 'Operator assigned',
  'conversations.unassigned': 'Unassigned',
  'conversations.csat.all': 'All',
  'conversations.csat.rated': 'Visitor rating',
  'conversations.csat.positive': 'Positive rating',
  'conversations.csat.negative': 'Negative rating',
  // ANX-047 — assign filter tabs
  'conversations.assignFilter.all': 'All',
  'conversations.assignFilter.me': 'Mine',
  'conversations.assignFilter.unassigned': 'Unassigned',
  'conversations.openInView': 'Open',
  'conversations.loadError': 'Failed to load conversations',
  'conversations.searchPlaceholder': 'Search by name, email, messages…',
  'conversations.searchClear': 'Clear search',
  'conversations.emptySearch': 'No matches',
  'tags.add': 'Add tag',
  'tags.removeChip': 'Remove tag',
  'tags.empty': 'No tags',
  'tags.searchOrCreate': 'Find or create a tag…',
  'tags.create': 'Create',
  'tags.filterTitle': 'Filter by tag',
  'tags.filterAll': 'All',
  'tags.adminTitle': 'Conversation tags',
  'tags.adminHint': 'Categories for grouping conversations — sales, support, VIP, spam, later.',
  'tags.namePlaceholder': 'Tag name',
  'tags.colorLabel': 'Color',
  'tags.confirmDelete': 'Delete tag',
  'tags.error.nameRequired': 'Please provide a tag name',
  'tags.error.nameTaken': 'That tag name is already taken',
  'departments.adminTitle': 'Departments',
  'departments.adminHint': 'Teams visitors can pick in the pre-chat form (sales, support, billing). Used for routing.',
  'departments.empty': 'No departments yet',
  'departments.namePlaceholder': 'Department name',
  'departments.confirmDelete': 'Delete department',
  'departments.error.nameRequired': 'Please provide a department name',
  'departments.error.nameTaken': 'That department name is already taken',
  'conversations.status.open': 'Open',
  'conversations.status.closed': 'Closed',
  'conversations.loadingAria': 'Loading conversations',
  'conversations.visitor.fallback': 'Visitor',
  'conversations.visitor.noContacts': 'No contacts shared',
  'conversations.visitor.copy': 'Copy',
  'conversations.visitor.copied': 'Copied',
  'conversations.visitor.email': 'Email',
  'conversations.visitor.phone': 'Phone',

  // Inbox placeholder
  'inbox.empty.title': 'Pick a conversation',
  'inbox.empty.hint': 'Your chats live in the list on the left. Click one to open it.',
  'inbox.empty.noneOpen': 'No open chats yet — we will notify you when one comes in.',

  // Signup
  'signup.title': 'Sign up for Anexica',
  'signup.subtitle': 'Create your account in under a minute — no card required.',
  'signup.email': 'Email',
  'signup.password': 'Password',
  'signup.passwordHint': 'At least 8 characters',
  'signup.tenantName': 'Company name',
  'signup.tenantNameHint': 'For example, “Acme Corp” — appears in your URL and UI',
  'signup.submit': 'Create account',
  'signup.submitting': 'Creating account…',
  'signup.loginLink': 'Already have an account? Sign in',
  'signup.error.invalidPayload': 'Please fill all fields, password ≥ 8 characters',
  'signup.error.rateLimited': 'Too many signups from this IP, please wait an hour',
  'signup.error.slugTaken': 'Could not create account, please try again',
  'signup.error.network': 'Network problem — check your connection',
  'signup.error.generic': 'Sign-up failed',
  'signup.error.captchaRequired': 'Please confirm you are human',
  'signup.error.captchaFailed': 'Captcha verification failed, try again',
  'signup.error.captchaUnavailable': 'Captcha service unavailable, try later',
  'signup.error.consentRequired': 'Please confirm consent to personal data processing',
  'signup.consent.label': 'I agree to the processing of my personal data and have read the',
  'signup.consent.policy': 'privacy policy',
  'signup.consent.glue': ' and accept the ',
  'signup.consent.terms': 'terms of use',

  // Onboarding
  'onboarding.title': 'Connect Anexica to your site',
  'onboarding.subtitle': 'Three steps — and your first message lands right here.',
  'onboarding.step1.title': 'Drop the snippet on your site',
  'onboarding.step1.hint': 'Copy the code and paste it before the closing </body> on any page.',
  'onboarding.step1.copy': 'Copy snippet',
  'onboarding.step1.copied': 'Copied!',
  'onboarding.step2.title': 'Open your site in a browser',
  'onboarding.step2.hint': 'The widget shows up in the corner — click it and send a test message.',
  'onboarding.step3.title': 'See your first message here',
  'onboarding.step3.hint': 'Once it arrives, we will check this step off and open the conversation.',
  'onboarding.step3.waiting': 'Waiting for the first message…',
  'onboarding.step3.done': 'Message received! Opening the conversation.',
  'onboarding.allDone': 'Done! Anexica is connected.',
  'onboarding.goToInbox': 'Go to inbox',
  'onboarding.skip': 'Skip — I will do this later',

  // Conversation view
  'conversation.title': 'Conversation',
  'conversation.send': 'Send',
  'conversation.placeholder': 'Operator reply (or /template)…',
  'conversation.uploadingPlaceholder': 'Uploading…',
  'conversation.assignToMe': 'Take',
  // ANX-047 — assignment-control
  'conversation.assigned.me': 'Assigned to me',
  'conversation.assigned.other': 'Assigned',
  'conversation.assigned.none': 'Unassigned',
  'conversation.assign.takeMe': 'Take it',
  'conversation.assign.unassign': 'Unassign',
  'conversation.close': 'Close conversation',
  'conversation.closed': 'Closed',
  'conversation.reopen': 'Reopen',
  'conversation.attachFile': 'Attach file',
  'conversation.emoji': 'Emoji',
  'conversation.dropHint': 'Drop file to attach',
  'conversation.search.open': 'Search in conversation',
  'conversation.search.placeholder': 'Search in conversation…',
  'conversation.search.prev': 'Previous match',
  'conversation.search.next': 'Next match',
  'conversation.search.close': 'Close search',
  'conversation.search.noResults': 'no matches',
  'conversation.visitorTyping': 'visitor is typing…',
  'conversation.visitorTypingAria': 'visitor is typing',
  'conversation.csat.label': 'Visitor rating:',
  'conversation.department.label': 'Department:',
  'conversation.note.modeLabel': 'Message mode',
  'conversation.note.modeReply': 'Reply',
  'conversation.note.modeNote': 'Note',
  'conversation.note.hint': 'visible to your team only',
  'conversation.note.placeholder': 'Internal note… @name to mention a teammate',
  'conversation.note.send': 'Save',
  'conversation.note.author': 'Operator',
  'conversation.note.badge': '· internal note',
  'conversation.note.error': 'Could not save the note',
  'context.title': 'About visitor',
  'context.page': 'Current page',
  'context.referrer': 'Referrer',
  'context.location': 'Location',
  'context.device': 'Device',
  'context.screen': 'Screen',
  'context.language': 'Language',
  'context.attributes': 'Attributes',
  'context.history': 'Past conversations',
  'context.noHistory': 'First conversation',
  'conversation.online': 'online',
  'conversation.connecting': 'connecting…',
  'conversation.offline': 'offline',
  'conversation.notFound': 'Conversation not found',
  'conversation.loadError': 'Failed to load conversation',
  'conversation.sendError': 'failed to send',
  'conversation.uploadError': 'failed to upload',
  'conversation.uploadTooLarge': 'File is too large',
  'conversation.sending': 'sending…',
  'conversation.failed': 'not sent',
  'conversation.read': 'Read',
  'conversation.delivered': 'Delivered',
  'conversation.reply': 'Reply',
  'conversation.reply.you': 'You',
  'conversation.reply.visitor': 'Visitor',
  'conversation.reply.unavailable': 'message unavailable',
  'conversation.reply.cancel': 'Cancel reply',

  // Analytics
  'analytics.title': 'Analytics',
  'analytics.openConversations': 'Open conversations',
  'analytics.openConversationsHint': 'now',
  'analytics.firstResponseP50': 'First response p50',
  'analytics.firstResponseP95': 'First response p95',
  'analytics.avgResponse': 'Average response time',
  'analytics.chatsPerDay': 'Conversations per day',
  'analytics.heatmap.title': 'Busiest hours',
  'analytics.heatmap.hint': 'New conversations by weekday and hour (in your timezone). Darker means more.',
  'analytics.activeOperators': 'Active operators',
  'analytics.operator': 'Operator',
  'analytics.chatsCount': 'Conversations',
  'analytics.csat': 'CSAT',
  'analytics.noData': 'No data for the selected period',
  'analytics.error': 'Failed to load analytics',
  'analytics.site': 'Site',
  'analytics.siteAll': 'All sites',
  'analytics.noOperatorsForPeriod': 'No operator replied in the selected period.',

  // Canned responses
  'canned.title': 'Canned responses',
  'canned.create': '+ New template',
  'canned.edit': 'Edit',
  'canned.editTitle': 'Edit template',
  'canned.newTitle': 'New template',
  'canned.delete': 'Delete',
  'canned.shortcut': 'Shortcut',
  'canned.shortcutHint': 'Shortcut (optional, starts with /)',
  'canned.body': 'Body',
  'canned.bodyHint': 'Inserted into the message input when picked.',
  'canned.titleField': 'Title',
  'canned.scope': 'Scope',
  'canned.scope.personal': 'personal',
  'canned.scope.shared': 'shared',
  'canned.scope.personalLong': 'Personal (only you)',
  'canned.scope.sharedLong': 'Shared (all operators)',
  'canned.scope.sharedHint': 'Visible to all operators in the tenant',
  'canned.empty': 'No templates yet. Create the first one — it will appear here.',
  'canned.shortcutTaken': 'This shortcut is already in use',
  'canned.confirmDelete': 'Delete this template?',
  'canned.saveError': 'Save failed',
  'canned.loadError': 'Failed to load templates',

  // Exports
  'exports.title': 'Chat exports',
  'exports.intro': 'Export messages to CSV or JSON. Files live for 24 hours.',
  'exports.create': '+ New export',
  'exports.newTitle': 'New export',
  'exports.format': 'Format',
  'exports.format.csv': 'CSV',
  'exports.format.json': 'JSON',
  'exports.range.from': 'From',
  'exports.range.to': 'To',
  'exports.site': 'Site',
  'exports.siteAll': 'All sites',
  'exports.status.queued': 'queued',
  'exports.status.processing': 'processing',
  'exports.status.ready': 'ready',
  'exports.status.failed': 'failed',
  'exports.status.expired': 'expired',
  'exports.column.created': 'Created',
  'exports.column.format': 'Format',
  'exports.column.status': 'Status',
  'exports.column.rows': 'Messages',
  'exports.column.size': 'Size',
  'exports.download': 'Download',
  'exports.empty': 'No exports yet.',
  'exports.emptyHintAdmin': 'Click the button above to create the first one.',
  'exports.emptyHintOperator': 'Only admin can create exports.',
  'exports.creating': 'Creating…',
  'exports.submitCreate': 'Create',
  'exports.createError': 'Failed to create export',
  'exports.tooManyActive': 'Too many active exports',
  'exports.refreshHint': 'List refreshes automatically',

  // Sites
  'sites.title': 'Sites',
  'sites.create': 'Create',
  'sites.name': 'Name',
  'sites.domain': 'Domain',
  'sites.domainOptional': 'Domain (opt.)',
  'sites.widgetKey': 'Widget key',
  'sites.copyKey': 'copy',
  'sites.copied': 'copied',
  'sites.edit': 'Edit',
  'sites.save': 'Save',
  'sites.cancel': 'Cancel',
  'sites.pause': 'Pause',
  'sites.resume': 'Resume',
  'sites.delete': 'Delete',
  'sites.confirmDelete': 'Delete site "{name}"?',
  'sites.theme.color': 'Accent color',
  'sites.theme.greeting': 'Greeting',
  'sites.theme.greetingPlaceholder': 'Default: "Hi there! 👋"',
  'sites.theme.right': 'Right',
  'sites.theme.left': 'Left',
  'sites.theme.save': 'Save widget',
  'sites.transcripts.title': 'Email transcript to visitor',
  'sites.followUp.title': 'Follow-up email for abandoned chats',
  'sites.followUp.hint': 'If the visitor goes offline and the operator replies, the visitor gets a “you have a reply” email (when email and consent are present). One email per conversation.',
  // ANX-065 — Telegram channel.
  'sites.telegram.title': 'Telegram bot',
  'sites.telegram.hint': 'Connect a bot (@BotFather → token) — incoming Telegram messages land in this same inbox, replies go back to the bot.',
  'sites.telegram.connected': 'Connected',
  'sites.telegram.connect': 'Connect',
  'sites.telegram.connecting': 'Connecting…',
  'sites.telegram.disconnect': 'Disconnect',
  'sites.telegram.tokenPlaceholder': 'Bot token from @BotFather',
  'conversations.channel.telegram': 'Conversation from Telegram',
  'sites.transcripts.hint': 'After a conversation is closed, the visitor receives a copy by email if they shared their email and opted in via the widget.',
  'conversation.transcript.sendToMe': 'Email me a copy',
  'conversation.transcript.sending': 'Sending…',
  'conversation.transcript.sent': 'Sent',
  'conversation.transcript.error': 'Failed to send',
  'sites.empty': 'No sites',
  'sites.saveError': 'Failed to save site',
  'sites.saving': 'Saving…',
  'sites.saved': 'Saved',
  'sites.loadError': 'Failed to load sites',
  // ANX-051 — pro-active trigger
  'sites.proactive.title': 'Auto-greeting in the widget',
  'sites.proactive.hint': 'After the configured delay the widget opens by itself with an operator-style greeting. Good for conversion; the visitor can dismiss it and it won’t reappear in the same session.',
  'sites.proactive.enabledLabel': 'Enable auto-open',
  'sites.proactive.delayLabel': 'Delay, seconds',
  'sites.proactive.messageLabel': 'Greeting text',
  'sites.proactive.messagePlaceholder': 'Hi! Can I help?',
  'sites.proactive.mobileLabel': 'Show on mobile too (off by default — the full-screen mobile widget would cover the page content and feel intrusive)',
  'sites.proactive.delayError': 'Delay must be between 5 and 3600 seconds',
  'sites.proactive.messageError': 'Message must be at most 280 characters',

  // ANX-061 — office hours + offline mode
  'sites.office.title': 'Office hours',
  'sites.office.hint': 'Outside office hours the widget shows an offline message and collects email. “Currently open” is computed in the visitor’s browser using the site timezone.',
  'sites.office.enabledLabel': 'Enable office hours',
  'sites.office.tzLabel': 'Timezone (IANA)',
  'sites.office.tzError': 'Enter a timezone, e.g. Europe/Moscow',
  'sites.office.intervalError': 'Start of the working day must be before the end',
  'sites.office.messageLabel': 'Offline message',
  'sites.office.messagePlaceholder': 'We’re away right now. Leave your email and we’ll reply during business hours.',
  'sites.office.messageError': 'Message must be at most 280 characters',
  'sites.office.collectEmailLabel': 'Collect visitor email outside office hours',
  'sites.prechat.title': 'Pre-chat form',
  'sites.prechat.hint': 'Ask visitors for details before the chat starts. Off by default.',
  'sites.prechat.enabledLabel': 'Show a pre-chat form before the conversation',
  'sites.prechat.field.name': 'Name',
  'sites.prechat.field.email': 'Email',
  'sites.prechat.field.phone': 'Phone',
  'sites.prechat.field.question': 'Question',
  'sites.prechat.requiredLabel': 'Required',
  'sites.prechat.departmentLabel': 'Let the visitor pick a department',
  'sites.prechat.noDepartments': 'No departments configured. Create them in Settings → Departments to enable this.',
  'sites.prechat.introLabel': 'Intro text',
  'sites.prechat.introPlaceholder': 'Shown above the form',
  'sites.prechat.introError': 'Intro must be ≤280 characters',
  'sites.office.fromLabel': 'from',
  'sites.office.toLabel': 'to',
  'sites.office.day.mon': 'Monday',
  'sites.office.day.tue': 'Tuesday',
  'sites.office.day.wed': 'Wednesday',
  'sites.office.day.thu': 'Thursday',
  'sites.office.day.fri': 'Friday',
  'sites.office.day.sat': 'Saturday',
  'sites.office.day.sun': 'Sunday',

  // Webhooks
  'webhooks.title': 'Webhooks',
  'webhooks.create': 'Create webhook',
  'webhooks.name': 'Name',
  'webhooks.url': 'URL',
  'webhooks.events': 'Events',
  'webhooks.secret': 'Secret',
  'webhooks.secretBanner': 'Secret for endpoint {id} (shown only once!):',
  'webhooks.active': 'Active',
  'webhooks.inactive': 'Disabled',
  'webhooks.pause': 'Pause',
  'webhooks.resume': 'Resume',
  'webhooks.delete': 'Delete',
  'webhooks.confirmDelete': 'Delete webhook "{name}"?',
  'webhooks.empty': 'No webhook endpoints',
  'webhooks.saveError': 'Failed to save webhook',
  'webhooks.loadError': 'Failed to load webhooks',
  'webhooks.event.messageNew': 'message.new',
  'webhooks.event.conversationAssigned': 'conversation.assigned',
  'webhooks.event.conversationClosed': 'conversation.closed',

  // Settings
  'settings.title': 'Settings',
  'settings.name': 'Name',
  'settings.email': 'Email',
  'settings.role': 'Role',
  'settings.operatorId': 'Operator ID',
  'settings.tenantId': 'Tenant ID',
  'settings.passwordHint': 'Password change is available via the "Forgot password" flow on the sign-in page.',
  'settings.language': 'Interface language',
  'settings.language.ru': 'Русский',
  'settings.language.en': 'English',
  'settings.push.title': 'Push notifications',
  'settings.push.unsupported': 'Browser does not support push notifications',
  'settings.push.serverDisabled': 'Push notifications are disabled on the server',
  'settings.push.permissionDenied': 'Push is blocked in browser settings — please allow it manually',
  'settings.push.subscribed': 'Push notifications are enabled on this device',
  'settings.push.subscribedHint': 'New visitor messages will arrive even when the tab is closed',
  'settings.push.idle': 'Push notifications are off',
  'settings.push.enable': 'Enable push',
  'settings.push.disable': 'Disable push',
  'settings.push.working': 'Working…',
  'settings.push.failed': 'Failed to change push setting',

  // Security / 2FA (passkey, ANX-013)
  'security.section.title': 'Two-factor authentication',
  'security.section.intro': 'Protect sign-in with your device — Touch ID, Face ID, Windows Hello, or a hardware key.',
  'security.passkeys.heading': 'Registered passkeys',
  'security.passkeys.empty': 'No passkeys yet. Add the first one — we will issue recovery codes.',
  'security.passkey.register': 'Register passkey',
  'security.passkey.registering': 'Confirm on your device…',
  'security.passkey.namePrompt': 'Device name (e.g. "MacBook"):',
  'security.passkey.defaultName': 'My passkey',
  'security.passkey.created': 'Added:',
  'security.passkey.lastUsed': 'Last used:',
  'security.passkey.delete': 'Remove',
  'security.passkey.confirmDelete': 'Remove this passkey? If it is the last one, make sure you still have recovery codes.',
  'security.passkey.cannotDeleteLast': 'This is your last passkey and the tenant requires MFA. Disable the policy or add another passkey first.',
  'security.passkey.unsupported': 'This browser does not support passkeys (WebAuthn).',
  'security.passkey.disabled': 'Passkeys are temporarily unavailable (WEBAUTHN_RPID is not configured on the server).',
  'security.passkey.error': 'Failed to register passkey',
  'security.recovery.heading': 'Recovery codes',
  'security.recovery.intro': 'Use these if you lose all your devices. Each code works once.',
  'security.recovery.regenerate': 'Generate new codes',
  'security.recovery.regenerating': 'Generating…',
  'security.recovery.warning': 'Save these codes now — you will not see them again after closing this dialog.',
  'security.recovery.copy': 'Copy',
  'security.recovery.copied': 'Copied',
  'security.recovery.done': 'Done',
  'security.recovery.error': 'Failed to generate recovery codes',
  'security.recovery.previewTitle': 'Your new recovery codes',

  // Tenant security policy (admin only)
  'security.policy.heading': 'Tenant security policy',
  'security.policy.requireMfa': 'Require passkey for all operators',
  'security.policy.requireMfaHint': 'When enabled, operators without a passkey will be forced to register one on next sign-in.',
  'security.policy.saving': 'Saving…',
  'security.policy.saveError': 'Failed to update policy',
  'security.policy.adminOnly': 'Tenant admins only',
  // ANX-071: operator load limit
  'security.policy.maxConcurrent': 'Per-operator concurrent conversation limit',
  'security.policy.maxConcurrentHint': '0 — no limit. At the limit the operator stops receiving auto-assignments (ANX-070).',
  'security.policy.routing': 'Auto-assign new conversations',
  'security.policy.routing.off': 'Off (manual assignment)',
  'security.policy.routing.roundRobin': 'Round-robin',
  'security.policy.routing.leastBusy': 'Least busy',
  'security.policy.routingHint': 'New conversations are auto-assigned to an available online operator (respecting the concurrency limit). No one available → stays unassigned.',
  'security.policy.sla': 'First-response SLA (minutes)',
  'security.policy.slaHint': '0 — off. Conversations awaiting a reply longer than this alert the assigned operator (or admins) via web-push.',
  'security.policy.followUpDelay': 'Follow-up email delay (minutes)',
  'security.policy.followUpDelayHint': 'How long to wait after the operator replies before emailing an abandoned visitor. Enabled per site.',
  'security.policy.slaRespectOfficeHours': 'Do not alert outside the site’s office hours',
  // ANX-068 — scheduled reports.
  'reports.title': 'Reports',
  'reports.subtitle': 'Archive of periodic digests of conversation metrics.',
  'reports.adminOnly': 'Reports are available to admins only.',
  'reports.empty': 'No reports yet',
  'reports.emptyHint': 'Enable a schedule in settings — the first report arrives after the period ends.',
  'reports.selectHint': 'Select a report on the left to see its metrics.',
  'reports.loadError': 'Failed to load the report',
  'reports.sentTo': 'sent to {n} recipients',
  'reports.notEmailed': 'not emailed',
  'reports.chatsN': '{n} chats',
  'reports.frequency.daily': 'Daily',
  'reports.frequency.weekly': 'Weekly',
  'reports.frequency.monthly': 'Monthly',
  'reports.metric.totalChats': 'New conversations',
  'reports.metric.firstResponse': 'First response',
  'reports.metric.avgResponse': 'Avg. response time',
  'reports.metric.csat': 'CSAT',
  'reports.metric.busiest': 'Busiest hour',
  'reports.section.operators': 'By operator',
  'reports.section.topTags': 'Top tags',
  'reports.schedule.heading': 'Scheduled reports',
  'reports.schedule.hint': 'Periodic metrics digest by email + an archive in this panel.',
  'reports.schedule.enabled': 'Send a scheduled report',
  'reports.schedule.frequency': 'Frequency',
  'reports.schedule.recipients': 'Recipients',
  'reports.schedule.recipientsPlaceholder': 'boss@example.com\nops@example.com',
  'reports.schedule.recipientsHint': 'One email per line (or comma-separated). May be empty — the report stays in the panel only.',
  'reports.schedule.timezone': 'Timezone (for busiest-hour)',
  'reports.schedule.saving': 'Saving…',
  'reports.schedule.saveError': 'Failed to save the schedule',
  'reports.schedule.nextRun': 'Next report',

  // ANX-105 — paywall upsell on 402 plan-limit.
  'paywall.title': 'Plan limit reached',
  'paywall.seat_limit_reached':
    'Your current plan includes a limited number of operators. Upgrade to invite more.',
  'paywall.dialog_limit_reached':
    "You've reached this plan's monthly conversation limit. Upgrade to keep receiving chats.",
  'paywall.plan_required':
    'This feature is available on paid plans. Upgrade your plan to enable it.',
  'paywall.cta': 'View plans',

  // ANX-060 layer 4 — Billing page.
  'billing.title': 'Billing',
  'billing.subtitle': 'Current plan, usage and payment.',
  'billing.adminOnly': 'Billing management is available to administrators only.',
  'billing.loadError': 'Failed to load billing data.',
  'billing.currentPlan': 'Current plan',
  'billing.periodEnd': 'paid until',
  'billing.plan.free': 'Free',
  'billing.plan.pro': 'Pro',
  'billing.plan.business': 'Business',
  'billing.plan.enterprise': 'Enterprise',
  'billing.status.none': 'no subscription',
  'billing.status.pending': 'awaiting payment',
  'billing.status.active': 'active',
  'billing.status.canceled': 'canceled',
  'billing.status.pastDue': 'past due',
  'billing.usage.heading': 'Usage',
  'billing.usage.seats': 'Operators',
  'billing.usage.dialogs': 'Dialogs this month',
  'billing.usage.unlimited': 'unlimited',
  'billing.paywall.warn':
    'Used {used} of {limit} dialogs this month — close to the Free plan limit.',
  'billing.paywall.over':
    'Free plan dialog limit ({limit}/mo) reached. Upgrade to a paid plan to lift the limit.',
  'billing.upgradeHeading': 'Change plan',
  'billing.perMonth': '/ mo',
  'billing.seats': '{n} operators',
  'billing.seatsUnlimited': 'unlimited operators',
  'billing.subscribeCard': 'Pay by card',
  'billing.subscribeAria': 'Pay by card — {plan}',
  'billing.subscribing': 'Redirecting…',
  'billing.subscribeError': 'Could not start payment. Try again later.',
  'billing.currentPlanBadge': 'Current plan',
  'billing.enterpriseContact': 'Contact us',
  'billing.enterpriseAria': 'Contact us about Enterprise',
  'billing.cancelSubscription': 'Cancel subscription',
  'billing.canceling': 'Canceling…',
  'billing.cancelError': 'Could not cancel the subscription.',
  'billing.returnPaid': 'Payment succeeded — plan activated.',
  'billing.returnPaidPending': 'Payment is processing. Refresh in a minute.',
  'billing.returnFailed': 'Payment was not completed. Please try again.',

  // ANX-060 layer 5 — pay by invoice (RU bank transfer).
  'billing.inv.heading': 'Pay by invoice',
  'billing.inv.hint':
    'For companies and sole proprietors: payer details, invoice and act as PDF, payment by bank transfer. Once paid, mark the invoice as paid — the plan activates.',
  'billing.inv.requisitesHeading': 'Payer details',
  'billing.inv.kind': 'Type',
  'billing.inv.kindLegal': 'Company',
  'billing.inv.kindIp': 'Sole proprietor',
  'billing.inv.name': 'Name',
  'billing.inv.inn': 'INN',
  'billing.inv.kpp': 'KPP',
  'billing.inv.address': 'Legal address',
  'billing.inv.email': 'Email for documents',
  'billing.inv.saveError': 'Could not save the details.',
  'billing.inv.issueHeading': 'New invoice',
  'billing.inv.issuePlan': 'Plan for invoice',
  'billing.inv.issueButton': 'Issue invoice',
  'billing.inv.issuing': 'Issuing…',
  'billing.inv.issueError': 'Could not issue the invoice.',
  'billing.inv.needRequisites': 'Fill in and save the payer details first.',
  'billing.inv.listHeading': 'Issued invoices',
  'billing.inv.empty': 'No invoices yet.',
  'billing.inv.colNumber': '#',
  'billing.inv.colPlan': 'Plan',
  'billing.inv.colPeriod': 'Period',
  'billing.inv.colAmount': 'Amount',
  'billing.inv.colStatus': 'Status',
  'billing.inv.colActions': 'Documents',
  'billing.inv.statusIssued': 'issued',
  'billing.inv.statusPaid': 'paid',
  'billing.inv.statusCanceled': 'canceled',
  'billing.inv.downloadInvoice': 'Invoice',
  'billing.inv.downloadAct': 'Act',
  'billing.inv.markPaid': 'Mark as paid',
  'billing.inv.marking': 'Marking…',
  'billing.inv.pdfError': 'Could not open the PDF.',

  // ANX-087 — product staff admin.
  'staff.brand': 'Anexica Admin',
  'staff.nav.dashboard': 'Dashboard',
  'staff.nav.tenants': 'Clients',
  'staff.nav.logout': 'Sign out',
  'staff.login.title': 'Admin sign in',
  'staff.login.email': 'Email',
  'staff.login.password': 'Password',
  'staff.login.signIn': 'Sign in',
  'staff.login.signingIn': 'Signing in…',
  'staff.login.failed': 'Wrong email or password.',
  'staff.login.suspended': 'Account is suspended.',
  'staff.login.mfaPrompt': 'Confirm sign-in with your passkey…',
  'staff.login.enrollPrompt': 'Register a passkey to sign in…',
  'staff.login.mfaFailed': 'Passkey verification failed.',
  'staff.loadError': 'Failed to load data.',
  'staff.actionError': 'Action failed.',
  'staff.impersonating': '(impersonating)',
  'staff.dashboard.title': 'Product overview',
  'staff.dashboard.byPlan': 'By plan',
  'staff.metric.mrr': 'MRR',
  'staff.metric.tenants': 'Clients',
  'staff.metric.active': 'Active',
  'staff.metric.suspended': 'Suspended',
  'staff.metric.operators': 'Operators',
  'staff.metric.dialogs30': 'Dialogs (30d)',
  'staff.metric.new30': 'New (30d)',
  'staff.col.plan': 'Plan',
  'staff.col.tenants': 'Clients',
  'staff.col.activeSubs': 'Active subs',
  'staff.col.mrrPart': 'MRR',
  'staff.col.client': 'Client',
  'staff.col.status': 'Status',
  'staff.col.seats': 'Operators',
  'staff.col.dialogsMonth': 'Dialogs/mo',
  'staff.col.created': 'Registered',
  'staff.tenants.search': 'Search by name/slug…',
  'staff.tenants.empty': 'No clients found.',
  'staff.status.active': 'active',
  'staff.status.suspended': 'suspended',
  'staff.status.deleted': 'deleted',
  'staff.detail.back': 'Back to clients',
  'staff.detail.impersonate': 'Sign in as client',
  'staff.detail.subscription': 'Subscription',
  'staff.detail.actions': 'Actions',
  'staff.detail.changePlan': 'Change plan',
  'staff.detail.apply': 'Apply',
  'staff.detail.grant': 'Grant plan',
  'staff.detail.days': 'Days',
  'staff.detail.grantBtn': 'Grant',
  'staff.detail.activate': 'Unsuspend',
  'staff.detail.suspend': 'Suspend',
  'staff.detail.delete': 'Delete',
  'staff.detail.deleteConfirm': 'Delete client? Access will be closed (soft-delete).',
  'staff.detail.operators': 'Operators',
  'staff.detail.noOperators': 'No operators.',
  'staff.detail.invoices': 'Invoices',

  'conversations.reopened': 'reopened',
  'conversations.awaitingReply': 'Awaiting operator reply',
  'conversation.transfer.button': 'Transfer',
  'conversation.transfer.selectOperator': 'Select a colleague…',
  'conversation.transfer.notePlaceholder': 'Reason (optional) — visible to operators',
  'conversation.transfer.submit': 'Transfer',
  'conversation.transfer.sending': 'Transferring…',

  // MFA login modal (second step after password)
  'auth.mfa.title': 'Confirm sign-in',
  'auth.mfa.subtitle': 'Sign in with a passkey or a recovery code',
  'auth.mfa.usePasskey': 'Use passkey',
  'auth.mfa.useRecovery': 'Use recovery code',
  'auth.mfa.starting': 'Confirm on your device…',
  'auth.mfa.recoveryPrompt': 'Enter your recovery code:',
  'auth.mfa.recoverySubmit': 'Sign in',
  'auth.mfa.error': 'Failed to confirm sign-in',
  'auth.mfa.invalidCode': 'Invalid or already-used code',

  // Forced enrollment (policy.require_mfa = true, not enrolled)
  'auth.enrollment.title': 'Passkey required',
  'auth.enrollment.subtitle': 'Your tenant admin enabled mandatory two-factor authentication. Register a passkey to continue.',
  'auth.enrollment.register': 'Register passkey',
  'auth.enrollment.registering': 'Confirm on your device…',
  'auth.enrollment.success': 'Done — passkey registered. Save your recovery codes.',
  'auth.enrollment.continue': 'Continue',
  'auth.enrollment.error': 'Failed to register passkey',

  // Plurals — {n} substituted automatically
  'unread.one': '{n} unread',
  'unread.few': '{n} unread',
  'unread.many': '{n} unread',
  'unread.other': '{n} unread',
  'messages.one': '{n} message',
  'messages.few': '{n} messages',
  'messages.many': '{n} messages',
  'messages.other': '{n} messages',
  'days.one': '{n} day',
  'days.few': '{n} days',
  'days.many': '{n} days',
  'days.other': '{n} days',

  // ANX-045 — audit-log UI
  'audit.title': 'Audit log',
  'audit.adminOnly': 'Admin role required.',
  'audit.filter.action': 'Event type',
  'audit.filter.all': 'All events',
  'audit.col.when': 'When',
  'audit.col.action': 'Event',
  'audit.col.actor': 'Actor',
  'audit.col.target': 'Target',
  'audit.col.metadata': 'Context',
  'audit.actor.system': 'system',
  'audit.actor.visitor': 'visitor',
  'audit.metadata.expand': 'show JSON',
  'audit.empty': 'No entries',
  'audit.loadError': 'Failed to load audit log',
  'audit.prev': 'Back',
  'audit.next': 'Next',
  'audit.event.site.created': 'Site created',
  'audit.event.site.updated': 'Site updated',
  'audit.event.site.deleted': 'Site deleted',
  'audit.event.operator.passkey_registered': 'Operator registered passkey',
  'audit.event.operator.passkey_deleted': 'Operator deleted passkey',
  'audit.event.operator.mfa_reset_by_admin': 'Admin reset operator MFA',
  'audit.event.operator.recovery_codes_regenerated': 'Recovery codes regenerated',
  'audit.event.tenant.policy_require_mfa_changed': 'require_mfa policy changed',
  'audit.event.invitation.created': 'Invitation created',
  'audit.event.invitation.accepted': 'Invitation accepted',
  'audit.event.webhook.created': 'Webhook created',
  'audit.event.webhook.updated': 'Webhook updated',
  'audit.event.webhook.deleted': 'Webhook deleted',
  'audit.event.conversation.closed': 'Conversation closed',
  'audit.event.visitor.erasure_requested': 'Visitor PII erasure requested',
  'audit.event.export.created': 'Export created',
  'audit.event.export.delivered': 'Export delivered',
  'audit.event.csp.violation': 'CSP violation reported by browser',

  // ANX-053 — notifications
  'notify.newMessage': 'New message',
  'notify.title': 'Notifications',
  'notify.sound': 'Sound on new message',
  'notify.browser': 'Browser notification on new message',
  'notify.permissionDefault': 'Browser has not yet asked for permission.',
  'notify.permissionGranted': 'Allowed',
  'notify.permissionDenied': 'Blocked in browser site settings. Open browser site settings to change.',
  'notify.permissionUnsupported': 'Browser does not support the Notification API.',
  'notify.requestPermission': 'Request permission',

  // ANX-059 — desktop app
  'desktop.title': 'Desktop app',
  'desktop.autostart': 'Launch on system startup',
  'desktop.autostartHint':
    'The app will automatically open in the system tray when you sign in.',

  // ANX-044 — keyboard shortcuts
  'hotkeys.title': 'Keyboard shortcuts',
  'hotkeys.nextConversation': 'Next conversation',
  'hotkeys.prevConversation': 'Previous conversation',
  'hotkeys.sendMessage': 'Send message',
  'hotkeys.focusCanned': 'Open canned responses',
  'hotkeys.closeConversation': 'Close current conversation',
  'hotkeys.focusSearch': 'Find a conversation',
  'hotkeys.showHelp': 'Show shortcuts list',

  // ANX-046 — visitor bans
  'ban.button': 'Ban',
  'ban.popover.title': 'Ban visitor',
  'ban.reason.placeholder': 'Reason (optional)',
  'ban.cancel': 'Cancel',
  'ban.confirm': 'Ban',
  'ban.saving': 'Saving…',
  'ban.error': 'Could not ban',
  'ban.ttl.1d': '24h',
  'ban.ttl.7d': '7d',
  'ban.ttl.30d': '30d',
  'ban.ttl.perm': 'Permanent',
  'bans.heading': 'Banned visitors',
  'bans.empty': 'No bans yet.',
  'bans.permanent': 'Permanent',
  'bans.policy.label': 'Who can ban',
  'bans.policy.adminOnly': 'Admin only',
  'bans.policy.anyOperator': 'Any operator',
  'bans.policy.hint': 'Admin can always ban. Operators only when set to "Any operator".',
  'bans.table.visitor': 'Visitor',
  'bans.table.ip': 'IP',
  'bans.table.reason': 'Reason',
  'bans.table.expiresAt': 'Until',
  'bans.table.status': 'Status',
  'bans.status.active': 'Active',
  'bans.status.expired': 'Expired',
  'bans.unban': 'Unban',
  'bans.unban.confirm': 'Confirm',

  // ANX-067 — activation onboarding checklist
  'activation.title': 'Set up Anexica',
  'activation.subtitle': 'A few steps to get chat live on your site',
  'activation.progress': '{done} of {total} done',
  'activation.dismiss': 'Dismiss',
  'activation.step.widget': 'Install the widget on your site',
  'activation.step.widget.hint': 'Widget not detected — paste the snippet on your site',
  'activation.step.widget.active': 'Widget live on {domain}',
  'activation.step.widget.activeNoDomain': 'Widget is live',
  'activation.step.team': 'Invite your team',
  'activation.step.team.hint': 'Add operators to answer together',
  'activation.step.officeHours': 'Set your office hours',
  'activation.step.officeHours.hint': 'Show visitors when you are online',
  'activation.step.theme': 'Customize the widget theme',
  'activation.step.theme.hint': 'Match the color and greeting to your brand',
  'activation.step.conversation': 'Get your first conversation',
  'activation.step.conversation.hint': 'Inquiries from your site will appear here',
  'sites.widget.detected': 'Widget is live',
  'sites.widget.detectedOn': 'Widget live on {domain}',
  'sites.widget.notDetected': 'Widget not detected',
};
